import { useGetTenant } from '@sunstone/api-client/hooks';
import { Loader2 } from 'lucide-react';
import { useParams } from 'react-router';
import { AccessBlockedPage } from '@/components/ui/AccessBlockedPage';
import { useAuthorization } from './useAuthorization';
import { useAuthToken } from './useAuthToken';

interface TenantGuardProps {
  children: React.ReactNode;
}

export function TenantGuard({ children }: TenantGuardProps) {
  const { tenantId: urlTenantId = '' } = useParams<{ tenantId: string }>();
  const { scope, tenantId: userTenantId } = useAuthorization();
  const { token } = useAuthToken();
  const skipAuth = import.meta.env.VITE_SKIP_AUTH === 'true';
  const { data, isLoading } = useGetTenant(skipAuth || !urlTenantId ? '' : urlTenantId, token);

  if (skipAuth || scope === 'all') {
    return <>{children}</>;
  }

  if (!urlTenantId) {
    return (
      <AccessBlockedPage
        title="Access Denied"
        message="You don't have permission to access this customer."
        action={{
          type: 'link',
          label: 'Go to your dashboard',
          to: userTenantId ? `/tenants/${userTenantId}` : '/',
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  const tenant = data?.tenant;

  const isOwnTenant = urlTenantId === userTenantId;
  const isOwnChildTenant =
    scope === 'partner' && !!tenant?.parentId && tenant.parentId === userTenantId;

  if (!isOwnTenant && !isOwnChildTenant) {
    return (
      <AccessBlockedPage
        title="Access Denied"
        message="You don't have permission to access this customer."
        action={{
          type: 'link',
          label: 'Go to your dashboard',
          to: userTenantId ? `/tenants/${userTenantId}` : '/',
        }}
      />
    );
  }

  return <>{children}</>;
}
